import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import PageModel from '@mm/state/model/PageModel';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { Alert, Heading } from '@mm/components';
import { CampaignTypeWithMappedPurposeAndDisplayStatus } from '@mm/state/selectors/campaignSelectors';
import { selectActiveChatbot } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { useQQWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-qq-widget-handlers';
import { filterCampaignWidgets } from '../../../components/helpers';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { CampaignErrors, OTN_PURPOSE_ID } from '../ChatBlasterDetailsPanel';
import { DialogueSelectorWithReload } from '../../../../startBotPromoters/common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { StartBotsDialogueEditor } from '../../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { successToast } from '../../../../../../common/helpers/toast';

type Props = {
  version?: string;
  campaign: CampaignTypeWithMappedPurposeAndDisplayStatus;
  dialogue: PageModel;
  purposeId?: number;
  errors: CampaignErrors;
  onDialogueChange?(dialogueId: number): void;
  usedInSlideout?: boolean;
  hideDialogueSelector?: boolean;
};
const noop = () => {};

export const ChatBlasterDialogueEditor = (props: Props) => {
  const {
    campaign,
    errors,
    onDialogueChange = noop,
    version,
    dialogue,
    purposeId,
    hideDialogueSelector,
    usedInSlideout,
  } = props;
  const chatbot = useSelector(selectActiveChatbot);
  const router = useRouter<{ dialogueId: string }>();
  const dialogueId = Number(dialogue?.id || router.match.params.dialogueId);
  const pathGraph = useNewGraphInstance(dialogue.id);
  const widgetsSource = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );

  const widgets = widgetsSource.map(widget => {
    if (widget.type !== WidgetTypes.DELAY) {
      return widget;
    }

    if (campaign.platforms?.includes('facebook') && purposeId === 200) {
      return {
        ...widget,
        metadata: Object.assign({}, widget.metadata, {
          alert: 'delay_blast_fb',
        }),
      };
    }

    return widget;
  });

  const { smsOnly, widgetsToExclude } = filterCampaignWidgets(
    campaign,
    widgets,
  );
  const platforms = {
    facebook: !!campaign.platforms?.includes('facebook'),
    sms: !!campaign.platforms?.includes('sms'),
  };
  const isOtn = purposeId === OTN_PURPOSE_ID;

  const [isAddingWidget, setisAddingWidget] = useState(false);
  const qqWidgetHandlers = useQQWidgetHandlers();

  const onInvalidInitialWidget = async () => {
    try {
      if (isOtn && !isAddingWidget) {
        const widget = await qqWidgetHandlers.addBaseQuestion(
          dialogueId,
          0,
          false,
          'Hey {{first name}}! The item you were interested in is back in stock! Want to check it out?',
        );
        setisAddingWidget(true);
        const res = await qqWidgetHandlers.addPostBackButton(widget, 'Yes');
        if (res) {
          successToast(
            `Quick Question successfully added to your selected dialogue`,
          );
        }
        setisAddingWidget(false);
      }
    } catch (err) {}
  };

  const dialogueSelectorDropdown = useMemo(() => {
    const campaignDialogueUpdate = async (newDialogueId: number) => {
      if (chatbot?.id) {
        const newUrl = BotBuilderRoutes.chatBlastEdit.getLink({
          chatbotId: chatbot?.id,
          dialogueId: newDialogueId,
          campaignId: campaign?.id,
        });

        onDialogueChange(newDialogueId);

        return {
          newDialogueId,
          newUrl,
        };
      }
    };

    return dialogueId && !hideDialogueSelector ? (
      <DialogueSelectorWithReload
        dialogueId={dialogueId}
        entityUpdateFunction={campaignDialogueUpdate}
        forFacebook
      />
    ) : null;
  }, [dialogueId, chatbot?.id, version, onDialogueChange]);

  const platformsArr = Object.entries(platforms)
    .filter(([_, v]) => v)
    .map(([k, v]) => k);

  return (
    <>
      <div className="customer-chat-dialogue-editor__header">
        <Heading size={3}>Choose a Dialogue to send</Heading>
      </div>

      <PathGraphContext.Provider value={pathGraph}>
        <>
          {chatbot && (
            <>
              {dialogueSelectorDropdown}
              {errors.dialogue && (
                <Alert
                  className="chat-blaster-steps__field-error"
                  type="error"
                  showCloseIcon={false}
                >
                  {errors.dialogue}
                </Alert>
              )}
              <StartBotsDialogueEditor
                widgets={widgets}
                dialogueId={dialogueId}
                widgetsToExclude={widgetsToExclude}
                chatbotId={chatbot?.id}
                isSmsRelated={platforms.sms}
                smsOnly={smsOnly}
                platforms={platformsArr}
                onInvalidInitialWidget={onInvalidInitialWidget}
                widgetAdderInitialAllowedWidgets={
                  isOtn
                    ? [
                        WidgetTypes.QUICK_QUESTION,
                        WidgetTypes.QQ_OPTIONS,
                        WidgetTypes.QQ_EMAIL,
                        WidgetTypes.QQ_PHONE,
                        WidgetTypes.QQ_INFO,
                      ]
                    : []
                }
                checkInitialAllowedWidgets={isOtn}
                disableFirstWidgetDeleting={isOtn}
                usedInSlideout={usedInSlideout}
              />
            </>
          )}
        </>
      </PathGraphContext.Provider>
    </>
  );
};
